'use client'

import { Button, Card, CardContent, CardFooter } from '@chain-zen/ui'
import Image from 'next/image'
import { useState } from 'react'

import { ANALYTICS_EVENTS } from '../../constants/analytics'
import { useAnalytics } from '../../contexts/analytics-context'
import { AppTypeEnum } from '../../types/workspace'
import type { TAppData } from './apps-data'
import { DisableAppModal } from './modals/disable-app-modal'
import { EmailRateConfirmationModal } from './modals/email-rate-confirmation-modal'

interface IAppCardProps {
  app: TAppData
  className?: string
  forCarrierOnboarding?: boolean
}

/**
 * This component is used to display a card for an app.
 * It is used in the apps page.
 * It is used to enable/disable an app.
 * It is used to update an app.
 * It is used to disable an app.
 * It is used to update an app.
 */
export function AppCard({ app, className, forCarrierOnboarding }: IAppCardProps) {
  const {
    appType,
    lightImageURL,
    darkImageURL,
    description,
    available,
    appName,
    workspaceApp,
    oAuthBaseURL,
    oAuthRedirectURISuffix,
    oAuthScope,
    modalSubtitle,
    modalTitle,
    isEnabled,
    isEditable,
    isPartiallyEnabled,
    bookingWorkspaceApp,
    portalWorkspaceApp,
    serviceWorkspaceApp,
    trackingWorkspaceApp
  } = app

  const [isDisableModalOpen, setIsDisableModalOpen] = useState(false)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalEditable, setIsModalEditable] = useState(false)

  const { captureEvent } = useAnalytics()

  const handleClick = () => {
    // Special handling for Expedite All app
    if (appType === AppTypeEnum.ExpediteAll || appType === AppTypeEnum.ThreePLSystems) {
      // Always open the enable modal (not disable modal) so user can manage both sub-apps
      setIsModalOpen(true)
    } else if (!isEnabled) {
      captureEvent(ANALYTICS_EVENTS.apps.enable, { app_name: appName })
      setIsModalOpen(true)
    } else if (isEnabled && appType === AppTypeEnum.EmailRateConfirmation) {
      setIsUpdateModalOpen(true)
    } else if (isEnabled) {
      setIsDisableModalOpen(true)
    }
  }

  const handleDisabled = () => {
    setIsDisableModalOpen(true)
    setIsUpdateModalOpen(false)
  }

  return (
    <Card
      className={`overflow-hidden transition-all duration-200 hover:shadow-lg hover:bg-accent/5 hover:scale-[1.01] ${className}`}
      data-testid={`app-card`}
    >
      <CardContent className="space-y-4">
        <div className="relative aspect-[16/9] bg-muted rounded-lg overflow-hidden">
          <Image
            src={darkImageURL || lightImageURL}
            alt={appType}
            className="object-cover"
            fill={true}
            loading="lazy"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-base">{appName}</h3>
            {!!isPartiallyEnabled && (
              <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-md border border-yellow-200">
                Partially Enabled
              </span>
            )}
          </div>
          {!forCarrierOnboarding && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </CardContent>

      <CardFooter>
        <div className="flex flex-col sm:flex-row w-full gap-2">
          {!!isEditable && !!isEnabled && (
            <Button
              variant="outline"
              className="w-full sm:flex-1"
              onClick={() => {
                setIsModalEditable(true)
                setIsModalOpen(true)
              }}
            >
              Update
            </Button>
          )}

          {forCarrierOnboarding && isEnabled ? (
            <Button className="w-full sm:flex-1 bg-green-500 text-white hover:bg-green-600" variant="outline">
              Enabled
            </Button>
          ) : (
            <Button
              variant={isEnabled ? 'destructive' : 'default'}
              className="w-full sm:flex-1"
              disabled={!available}
              onClick={handleClick}
            >
              {!available ? 'Coming soon...' : isEnabled ? 'Disable' : 'Enable'}
            </Button>
          )}
        </div>
      </CardFooter>

      <app.modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
        }}
        app={app}
        appType={appType}
        modalTitle={modalTitle}
        isModalEditable={isModalEditable}
        setModalEditable={setIsModalEditable}
        modalSubtitle={modalSubtitle}
        oAuthBaseURL={oAuthBaseURL}
        oAuthRedirectURISuffix={oAuthRedirectURISuffix}
        oAuthScope={oAuthScope}
        workspaceApp={workspaceApp}
        bookingWorkspaceApp={bookingWorkspaceApp}
        portalWorkspaceApp={portalWorkspaceApp}
        serviceWorkspaceApp={serviceWorkspaceApp}
        trackingWorkspaceApp={trackingWorkspaceApp}
      />

      {!!isDisableModalOpen && (
        <DisableAppModal isOpen={isDisableModalOpen} onClose={() => setIsDisableModalOpen(false)} app={app} />
      )}

      {!!isUpdateModalOpen && (
        <EmailRateConfirmationModal
          isOpen={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          app={app}
          onDisabled={handleDisabled}
        />
      )}
    </Card>
  )
}
